import { useState } from "react";

import type { PendingApproval } from "@/api/types";
import { ProtocolIdDisplay } from "@/components/ProtocolIdDisplay";
import { Button } from "@/components/ui/button";
import { useApprove, useReject } from "@/hooks/useApprovals";

interface Props {
  approval: PendingApproval;
  compact?: boolean;
}

export function ApprovalCard({ approval, compact = false }: Props) {
  const approveMutation = useApprove();
  const rejectMutation = useReject();
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState<"approve" | null>(null);

  const isResolved = approval.status !== "pending";
  const action = approval.action_payload;
  const esCorreo = approval.action_type.startsWith("correo_");
  const esZoom = approval.action_type.startsWith("zoom_");

  const handleApprove = () => {
    if (confirming !== "approve") {
      setConfirming("approve");
      return;
    }
    approveMutation.mutate(approval.id);
    setConfirming(null);
  };

  const handleReject = () => {
    rejectMutation.mutate({
      id: approval.id,
      reason: reason.trim() || undefined,
    });
    setShowRejectForm(false);
    setReason("");
  };

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950/40">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <span className="rounded bg-amber-200 px-2 py-0.5 text-xs uppercase tracking-wide text-amber-900 dark:bg-amber-800 dark:text-amber-100">
          {approval.status}
        </span>
        {esZoom ? (
          <span>Cambio en Zoom Phone</span>
        ) : esCorreo ? (
          <span>
            {approval.action_type === "correo_escalamiento"
              ? "Escalar aviso de falla"
              : "Contestar correo"}
          </span>
        ) : (
          <ProtocolIdDisplay internalId={action.playbook_id} />
        )}
      </div>
      {esCorreo && <CorreoDetalle action={action} />}
      {action.reason && (
        <div className="mb-2 text-sm">
          <span className="font-medium">Razón:</span> {action.reason}
        </div>
      )}
      {action.diff && (
        <pre className="mb-2 max-h-96 overflow-auto whitespace-pre rounded border bg-background p-2 font-mono text-xs">
          {action.diff}
        </pre>
      )}
      {!compact && action.incident_id && (
        <div className="mb-2 text-xs text-muted-foreground">
          Incidente: <span className="font-mono">{action.incident_id}</span>
        </div>
      )}
      {isResolved && (
        <div className="text-xs text-muted-foreground">
          {approval.decided_via && <>Decidido vía {approval.decided_via}. </>}
          {approval.rejection_reason && (
            <>Razón del rechazo: {approval.rejection_reason}</>
          )}
        </div>
      )}

      {!isResolved && (
        <div className="mt-3 space-y-2">
          {confirming === "approve" && (
            <div className="rounded border border-amber-400 bg-amber-100 p-2 text-xs dark:bg-amber-900/40">
              {esZoom ? (
                <>
                  Confirma: AROC aplicará este cambio en Zoom por la API (sólo
                  si la escritura está encendida) y lo volverá a leer para
                  confirmarlo. El resultado queda en Zoom → Plan vs Zoom.
                </>
              ) : esCorreo ? (
                <>
                  Confirma el borrador. Sólo se envía si el envío del buzón está
                  encendido para este caso.
                </>
              ) : (
                <>
                  Confirma: AROC ejecutará{" "}
                  <ProtocolIdDisplay internalId={action.playbook_id} /> en modo{" "}
                  <span className="font-mono">live</span>. Esta acción es real.
                </>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleApprove}
              disabled={approveMutation.isPending}
            >
              {confirming === "approve"
                ? esCorreo || esZoom
                  ? "Sí, aprobar"
                  : "Sí, ejecutar"
                : "Aprobar"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowRejectForm((v) => !v)}
              disabled={rejectMutation.isPending}
            >
              Rechazar
            </Button>
            {confirming === "approve" && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setConfirming(null)}
              >
                Cancelar
              </Button>
            )}
          </div>
          {showRejectForm && (
            <div className="space-y-2 rounded border bg-background p-2">
              <label className="text-xs font-medium">
                Razón (opcional, alimenta a AROC para que proponga alternativa)
              </label>
              <textarea
                className="min-h-[60px] w-full resize-none rounded-md border bg-background p-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ej: ventana de mantenimiento ya programada para las 22:00."
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleReject}
                  disabled={rejectMutation.isPending}
                >
                  Confirmar rechazo
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowRejectForm(false);
                    setReason("");
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CorreoDetalle({
  action,
}: {
  action: PendingApproval["action_payload"];
}) {
  return (
    <div className="mb-2 space-y-1 text-sm">
      <div>
        <span className="font-medium">De:</span> {action.de} ·{" "}
        <span className="font-medium">Asunto:</span> {action.asunto}
      </div>
      <div>
        <span className="font-medium">Caso:</span> {action.caso} ·{" "}
        <span className="font-medium">Veredicto:</span> {action.veredicto}
        {action.responsable && <> · Responsable: {action.responsable}</>}
      </div>
      {action.resumen && <div>{action.resumen}</div>}
      {action.alerta && (
        <div className="text-red-700 dark:text-red-300">
          Posible manipulación: {action.alerta}
        </div>
      )}
      {action.evidencia && action.evidencia.length > 0 && (
        <ul className="list-disc pl-5 text-xs">
          {action.evidencia.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      )}
      <div className="text-xs text-muted-foreground">
        Para: {(action.para ?? []).join(", ") || "(sin destinatario)"}
        {action.cc && action.cc.length > 0 && (
          <> · Cc: {action.cc.join(", ")}</>
        )}
      </div>
      <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded border bg-background p-2 text-xs">
        {action.borrador || "(sin borrador)"}
      </pre>
    </div>
  );
}
